import React, { useState } from "react";
import { useSpring, animated, config } from '@react-spring/web';
import Modal from "react-modal";
import "./partnerModal.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import emailjs from "@emailjs/browser";

const PartnerDemoModal = ({ isOpen, onRequestClose }) => {
  const [companyName, setCompanyName] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [emailError, setEmailError] = useState("");
  const [formError, setFormError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Modal animation
  const modalAnimation = useSpring({
    opacity: isOpen ? 1 : 0,
    transform: isOpen ? 'translateY(0px)' : 'translateY(20px)',
    config: { tension: 300, friction: 20 }
  });

  // Content animations (staggered)
  const headerAnimation = useSpring({
    opacity: isOpen ? 1 : 0,
    transform: isOpen ? 'translateY(0px)' : 'translateY(-10px)',
    delay: 100,
    config: { tension: 300, friction: 15 }
  });

  const formAnimation = useSpring({
    opacity: isOpen ? 1 : 0,
    transform: isOpen ? 'translateY(0px)' : 'translateY(10px)',
    delay: 200,
    config: { tension: 300, friction: 15 }
  });

  const buttonAnimation = useSpring({
    opacity: isOpen ? 1 : 0,
    transform: isOpen ? 'translateY(0px)' : 'translateY(10px)',
    delay: 300,
    config: { tension: 300, friction: 15 }
  });

  // Input field animations
  const inputAnimations = [
    useSpring({ opacity: isOpen ? 1 : 0, delay: 250, config: config.stiff }),
    useSpring({ opacity: isOpen ? 1 : 0, delay: 300, config: config.stiff }),
    useSpring({ opacity: isOpen ? 1 : 0, delay: 350, config: config.stiff }),
    useSpring({ opacity: isOpen ? 1 : 0, delay: 400, config: config.stiff }),
    useSpring({ opacity: isOpen ? 1 : 0, delay: 450, config: config.stiff }),
    useSpring({ opacity: isOpen ? 1 : 0, delay: 500, config: config.stiff })
  ];

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidMobile = (mobile) => /^[0-9]{10}$/.test(mobile);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form fields
    let isValid = true;
    setFormError("");
    setEmailError("");

    if (!companyName || !name || !email || !mobile || !date || !time) {
      setFormError("All fields are required.");
      isValid = false;
    }
    if (!isValidEmail(email)) {
      setEmailError("Invalid email format");
      isValid = false;
    }
    if (!isValidMobile(mobile)) {
      setFormError("Invalid mobile number (must be 10 digits)");
      isValid = false;
    }

    if (!isValid) return;

    const templateParams = {
      to_name: "Wikiworks Team",
      from_name: name,
      message: `You have a new demo booking from ${name}.\n\nDetails:\n- Company Name: ${companyName}\n- Name: ${name}\n- Email: ${email}\n- Mobile: ${mobile}\n- Date: ${date}\n- Time: ${time}`,
    };

    try {
      setIsLoading(true);
      await emailjs.send(
        "service_1e5xeqo",
        "template_6ees2pr",
        templateParams,
        "piFdp5IfrbeQZ9qA-"
      );

      toast.success("Demo booked successfully! We will contact you soon.", {
        position: "top-center",
        autoClose: 3000,
      });

      setCompanyName("");
      setName("");
      setEmail("");
      setMobile("");
      setDate("");
      setTime("");
      onRequestClose();
    } catch (error) {
      console.error("Error: ", error);
      toast.error("An error occurred while booking the demo.", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="book-demo-modal"
      overlayClassName="book-demo-overlay"
      ariaHideApp={false}
    >
      <animated.div style={modalAnimation} className="modal-content-wrapper">
        <animated.div style={headerAnimation} className="modal-header">
          <h2>Schedule a Meet</h2>
          <button 
            onClick={onRequestClose} 
            className="close-button"
            whileHover={{ scale: 1.1 }}
          >
            ×
          </button>
        </animated.div>

        <animated.form style={formAnimation} className="modal-form" onSubmit={handleSubmit}>
          <animated.div style={inputAnimations[0]} className="input-wrapper">
            <input
              type="text"
              name="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Company Name"
              required
            />
          </animated.div>

          <animated.div style={inputAnimations[1]} className="input-wrapper">
            <input
              type="text"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your Name"
              required
            />
          </animated.div>

          <animated.div style={inputAnimations[2]} className="input-wrapper">
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Official Email ID"
              required
            />
            {emailError && <p className="errorText">{emailError}</p>}
          </animated.div>

          <animated.div style={inputAnimations[3]} className="input-wrapper">
            <input
              type="tel"
              name="mobile"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="Mobile Number"
              required
            />
          </animated.div>

          <div className="date-time-row">
            <animated.div style={inputAnimations[4]} className="input-wrapper">
              <input
                type="date"
                name="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                placeholder="Date"
                required
              />
            </animated.div>

            <animated.div style={inputAnimations[5]} className="input-wrapper">
              <input
                type="time"
                name="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                placeholder="Time"
                required
              />
            </animated.div>
          </div>

          {formError && <p className="errorText">{formError}</p>}

          <animated.button 
            style={buttonAnimation}
            type="submit" 
            className="submit-button" 
            disabled={isLoading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isLoading ? "Submitting..." : "Submit"}
          </animated.button>
        </animated.form>

        {isLoading && <div className="loader"></div>}
        <ToastContainer position="top-left" />
      </animated.div>
    </Modal>
  );
};

export default PartnerDemoModal;